import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import ReactWebChat, { 
  createDirectLine, 
  createStore,
  createCognitiveServicesSpeechServicesPonyfillFactory
} from 'botframework-webchat';
import DraggableAvatarPod from "./components/DraggableAvatarPod";
import './App.css';
import { auth, googleProvider } from "./firebase";
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from "firebase/auth";

// Helper to detect Mobile devices & iOS Home Screen PWA Standalone Mode
// eslint-disable-next-line no-unused-vars
const isMobileOrPWA = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
  const isPwaStandalone = window.navigator.standalone === true || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
  return isMobile || isPwaStandalone;
};

// Retry logic for Cold State / Network Glitches
const fetchWithRetry = async (url, options, retries = 3, delay = 2000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, options);
      if (res.ok) return res;
      // If 500 (Server Error) or 504 (Timeout), throw to trigger retry
      if (res.status === 500 || res.status === 503 || res.status === 504) {
        throw new Error(`Server Error: ${res.status} `);
      }
      return res; // Return other errors (400, 401) normally
    } catch (err) {
      if (i === retries - 1) throw err;
      console.warn(`Fetch attempt ${i + 1} failed. Retrying...`, err);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};


// Strip markdown characters to prevent Azure Speech from reading them out loud
const stripMarkdown = (text) => {
  if (!text) return "";
  return text
    // Remove bold and italic formatting (e.g. **bold**, *italic*, __bold__, _italic_)
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    // Remove inline code block (e.g. `code`)
    .replace(/`([^`]+)`/g, "$1")
    // Remove links [text](url) -> keep text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // Remove headers (e.g. # Header -> Header)
    .replace(/^#+\s+/gm, "")
    // Remove bullet points (e.g. * Item -> Item, - Item -> Item)
    .replace(/^\s*[-*+]\s+/gm, "")
    // Remove numbered lists (e.g. 1. Item -> Item)
    .replace(/^\s*\d+\.\s+/gm, "")
    // Remove blockquotes (e.g. > Quote -> Quote)
    .replace(/^\s*>\s+/gm, "")
    // Clean up multiple spaces and newlines
    .replace(/\s+/g, " ")
    .trim();
};


function App() {
  const [user, setUser] = useState(null);
  const [quotaStatus, setQuotaStatus] = useState("loading"); // 'loading', 'allowed', 'blocked', 'error'
  const [quotaErrorMsg, setQuotaErrorMsg] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [debugLog, setDebugLog] = useState([]);

  const addLog = (msg) => setDebugLog(prev => [...prev, `${new Date().toISOString().split('T')[1]} - ${msg}`]);

  /* --- AUTH STATE (EMAIL/PW) --- */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);

  /* --- LANGUAGE STATE --- */
  const [language, setLanguage] = useState(null); // 'en' or 'es' or null (not selected)
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  /* --- VOICE ASSISTANT STATE & AVATAR SYNC --- */
  const [isVoiceMode] = useState(true);
  const [speechEnabled, setSpeechEnabled] = useState(true);
  const [ponyfillFactory, setPonyfillFactory] = useState(null);
  const activePonyfillRef = useRef(null);
  const activeSynthesizerRef = useRef(null);
  const speakingTimerRef = useRef(null);
  const [speechLoading, setSpeechLoading] = useState(isVoiceMode);
  const [visemeId, setVisemeId] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isAvatarWaving, setIsAvatarWaving] = useState(false);

  // Inactivity & Session Management
  const [isSessionActive, setIsSessionActive] = useState(true);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const lastActiveTimestampRef = useRef(Date.now());



  const [selectedVoice, setSelectedVoice] = useState(
    language === 'es' ? 'es-ES-ElviraNeural' : 'en-US-JennyNeural'
  );

  const selectedVoiceRef = useRef(selectedVoice);
  const speechEnabledRef = useRef(speechEnabled);

  // Global Audio Unlock Helper for Mobile Web Audio & Speech Synthesis
  const unlockAudio = useCallback(() => {
    try {
      const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
      if (AudioCtxClass) {
        if (!window.__globalAudioCtx || window.__globalAudioCtx.state === 'closed') {
          window.__globalAudioCtx = new AudioCtxClass();
        }
        if (window.__globalAudioCtx && window.__globalAudioCtx.state === 'suspended') {
          window.__globalAudioCtx.resume().catch(() => {});
        }
        // Play tiny silent buffer to warm up mobile hardware audio engine on iOS Safari / Chrome
        try {
          const buffer = window.__globalAudioCtx.createBuffer(1, 1, 22050);
          const source = window.__globalAudioCtx.createBufferSource();
          source.buffer = buffer;
          source.connect(window.__globalAudioCtx.destination);
          source.start(0);
        } catch (e) {}
      }
    } catch (e) {
      console.warn("AudioContext unlock warning:", e);
    }

    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        if (window.speechSynthesis.resume) {
          window.speechSynthesis.resume();
        }
      } catch (e) {}
    }

    if (activePonyfillRef.current && activePonyfillRef.current.speechSynthesis) {
      try {
        if (activePonyfillRef.current.speechSynthesis.resume) {
          activePonyfillRef.current.speechSynthesis.resume();
        }
      } catch (e) {}
    }
  }, []);

  // Attach one-time global user gesture listener to unlock mobile audio context
  useEffect(() => {
    const handleFirstUserGesture = () => {
      unlockAudio();
    };

    const events = ['touchstart', 'touchend', 'click', 'keydown', 'pointerdown'];
    events.forEach(evt => window.addEventListener(evt, handleFirstUserGesture, { capture: true, passive: true }));

    return () => {
      events.forEach(evt => window.removeEventListener(evt, handleFirstUserGesture, { capture: true }));
    };
  }, [unlockAudio]);

  // Immediately cancel active speech synthesis & return avatar to idle
  const handleStopSpeaking = useCallback(() => {
    if (speakingTimerRef.current) {
      clearTimeout(speakingTimerRef.current);
      speakingTimerRef.current = null;
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try { window.speechSynthesis.cancel(); } catch (e) {}
    }
    if (activePonyfillRef.current && activePonyfillRef.current.speechSynthesis) {
      try { activePonyfillRef.current.speechSynthesis.cancel(); } catch (e) {}
    }
    if (activeSynthesizerRef.current) {
      try {
        if (typeof activeSynthesizerRef.current.close === 'function') {
          activeSynthesizerRef.current.close();
        }
      } catch (e) {}
      activeSynthesizerRef.current = null;
    }
    setIsSpeaking(false);
    setVisemeId(0);
  }, []);

  // Inactivity & Session Management (5-min warning, 10-min session end)
  // Uses absolute timestamps & heartbeat interval + visibilitychange to guarantee reliability on iPad / iOS sleep
  const resetInactivityTimers = useCallback(() => {
    lastActiveTimestampRef.current = Date.now();
    setShowInactivityWarning(false);
  }, []);

  const checkInactivityState = useCallback(() => {
    if (!user || !isSessionActive) return;
    const now = Date.now();
    const elapsedMs = now - lastActiveTimestampRef.current;

    const WARNING_TIMEOUT = 5 * 60 * 1000;  // 5 Minutes: 300,000 ms
    const SESSION_TIMEOUT = 10 * 60 * 1000; // 10 Minutes: 600,000 ms

    if (elapsedMs >= SESSION_TIMEOUT) {
      setShowInactivityWarning(false);
      setIsSessionActive(false);
      handleStopSpeaking();
      setDirectLine(null);
      setPonyfillFactory(null);
    } else if (elapsedMs >= WARNING_TIMEOUT) {
      setShowInactivityWarning(true);
    } else {
      setShowInactivityWarning(false);
    }
  }, [user, isSessionActive, handleStopSpeaking]);

  useEffect(() => {
    if (!user || !isSessionActive) return;

    // Reset baseline timestamp on component mount / session start
    lastActiveTimestampRef.current = Date.now();

    // 1. Heartbeat check every 5 seconds (evaluates absolute elapsed time, unaffected by setTimeout throttling)
    const heartbeatInterval = setInterval(() => {
      checkInactivityState();
    }, 5000);

    // 2. Throttled activity listener (requires 2 seconds between updates to prevent touch/scroll spam)
    let lastEventTime = 0;
    const handleUserActivity = () => {
      const now = Date.now();
      const elapsedSinceActive = now - lastActiveTimestampRef.current;

      // If already past 10 minutes, immediately trigger session end
      if (elapsedSinceActive >= 10 * 60 * 1000) {
        checkInactivityState();
        return;
      }

      if (now - lastEventTime > 2000) {
        lastEventTime = now;
        lastActiveTimestampRef.current = now;
        setShowInactivityWarning(false);
      }
    };

    // 3. Page visibility & window focus handler (critical for iPad screen sleep/wake and tab switching)
    const handleVisibilityOrFocus = () => {
      checkInactivityState();
    };

    // Intent-driven gesture events (excluding continuous noisy mousemove & scroll)
    const activityEvents = ['pointerdown', 'touchstart', 'keydown', 'click'];
    activityEvents.forEach(evt => window.addEventListener(evt, handleUserActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('pageshow', handleVisibilityOrFocus);

    return () => {
      clearInterval(heartbeatInterval);
      activityEvents.forEach(evt => window.removeEventListener(evt, handleUserActivity));
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('pageshow', handleVisibilityOrFocus);
    };
  }, [user, isSessionActive, checkInactivityState]);



  // Helper to maintain solid, persistent speaking state across audio/viseme stream chunks
  const refreshSpeakingState = useCallback((newVisemeId) => {
    if (newVisemeId !== undefined && typeof newVisemeId === 'number') {
      setVisemeId(newVisemeId);
    }
    setIsSpeaking(true);
  }, []);

  // Sync speechEnabledRef & stop active speech if muted
  useEffect(() => {
    speechEnabledRef.current = speechEnabled;
    if (!speechEnabled) {
      handleStopSpeaking();
    }
  }, [speechEnabled, handleStopSpeaking]);

  // Sync selected voice when language changes
  useEffect(() => {
    setSelectedVoice(language === 'es' ? 'es-ES-ElviraNeural' : 'en-US-JennyNeural');
    // Trigger wave animation on avatar when switching languages or initial load
    setIsAvatarWaving(true);
    const waveTimer = setTimeout(() => setIsAvatarWaving(false), 2000);
    return () => clearTimeout(waveTimer);
  }, [language]);

  // Sync selectedVoiceRef when voice changes
  useEffect(() => {
    selectedVoiceRef.current = selectedVoice;
  }, [selectedVoice]);

  // selectVoice callback passed to WebChat — stable across voice changes to prevent chatStore re-creation
  const selectVoice = useCallback((voices) => {
    const targetVoice = selectedVoiceRef.current || (language === 'es' ? 'es-ES-ElviraNeural' : 'en-US-JennyNeural');
    if (voices && voices.length) {
      const matchedVoice = voices.find(v => 
        v.name === targetVoice || 
        v.voiceURI === targetVoice || 
        v.id === targetVoice
      );
      if (matchedVoice) return matchedVoice;
    }
    // Fallback constructed voice object so Azure Speech SDK ALWAYS receives targetVoice
    const langPrefix = language === 'es' ? 'es-ES' : 'en-US';
    return {
      name: targetVoice,
      voiceURI: targetVoice,
      id: targetVoice,
      lang: langPrefix
    };
  }, [language]);

  // Azure Speech Initialization Hook
  useEffect(() => {
    if (!isVoiceMode || !user || !isSessionActive) {
      setSpeechLoading(false);
      return;
    }

    let active = true;
    setSpeechLoading(true);

    const initAzureSpeech = async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch('/api/get-speech-token', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-App-Token': token,
            'Content-Type': 'application/json'
          }
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch Azure Speech Token: status ${res.status}`);
        }

        const { token: speechToken, region } = await res.json();
        
        if (!active) return;

        // Create base factory directly with custom silence timeouts for realistic human speaking pauses
        const baseFactory = await createCognitiveServicesSpeechServicesPonyfillFactory({
          credentials: {
            authorizationToken: speechToken,
            region: region
          },
          speechRecognitionInitialSilenceTimeoutMs: 15000, // 15 seconds before initial dictation timeout
          speechRecognitionEndSilenceTimeoutMs: 5000       // 5 seconds trailing pause allowed before dictation ends

        });


        // Wrap baseFactory to intercept SpeechSynthesizer viseme events & speechSynthesis lifecycle
        const avatarEnhancedFactory = (options) => {
          const ponyfill = baseFactory({
            ...options,
            selectVoice: selectVoice
          });
          if (ponyfill) {
            activePonyfillRef.current = ponyfill;
            // 1. Intercept Azure SpeechSynthesizer visemes if present
            if (ponyfill.SpeechSynthesizer) {
              const OriginalSynthesizer = ponyfill.SpeechSynthesizer;
              ponyfill.SpeechSynthesizer = function (...args) {
                const synth = new OriginalSynthesizer(...args);
                activeSynthesizerRef.current = synth;
                
                synth.visemeReceived = (s, e) => {
                  if (e && typeof e.visemeId === 'number') {
                    refreshSpeakingState(e.visemeId);
                  }
                };
                synth.synthesisCompleted = () => {
                  activeSynthesizerRef.current = null;
                };
                synth.SynthesisCanceled = () => {
                  activeSynthesizerRef.current = null;
                  handleStopSpeaking();
                };

                return synth;
              };
            }

            // 2. Intercept Web Speech API Utterances for start/end lifecycle
            if (ponyfill.speechSynthesis) {
              const origSpeak = ponyfill.speechSynthesis.speak.bind(ponyfill.speechSynthesis);
              ponyfill.speechSynthesis.speak = (utterance) => {
                if (!speechEnabledRef.current) {
                  return; // Muted: do not play audio
                }
                if (utterance) {
                  // Explicitly assign target voice on utterance so Azure Speech API uses chosen neural voice
                  const voices = ponyfill.speechSynthesis.getVoices ? ponyfill.speechSynthesis.getVoices() : [];
                  const matchedVoice = selectVoice(voices);
                  if (matchedVoice) {
                    utterance.voice = matchedVoice;
                  }

                  const origOnStart = utterance.onstart;
                  const origOnEnd = utterance.onend;
                  const origOnError = utterance.onerror;

                  utterance.onstart = (e) => {
                    setIsSpeaking(true);
                    setVisemeId(0);
                    if (origOnStart) origOnStart(e);
                  };

                  utterance.onend = (e) => {
                    setIsSpeaking(false);
                    setVisemeId(0);
                    if (origOnEnd) origOnEnd(e);
                  };

                  utterance.onerror = (e) => {
                    setIsSpeaking(false);
                    setVisemeId(0);
                    if (origOnError) origOnError(e);
                  };
                }
                return origSpeak(utterance);
              };
            }
          }
          return ponyfill;
        };

        setPonyfillFactory(() => avatarEnhancedFactory);

      } catch (err) {
        console.error("Failed to initialize Azure speech ponyfill, falling back to text mode:", err);
      } finally {
        if (active) {
          setSpeechLoading(false);
        }
      }
    };

    initAzureSpeech();

    return () => {
      active = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVoiceMode, user, isSessionActive]);
  /* --- CHAT STATE --- */
  const [directLine, setDirectLine] = useState(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [chatCount, setChatCount] = useState(0);
  const [monthlyChatLimit, setMonthlyChatLimit] = useState(300);

  /* --- MENU STATE --- */
  const [showDropdown, setShowDropdown] = useState(false);

  // Bot URLs (No longer used directly, replaced by generic proxy, but keeping for reference)
  // const BOT_URL_EN = "https://copilotstudio.microsoft.com/environments/...";
  // const BOT_URL_ES = "https://copilotstudio.microsoft.com/environments/...";

  // Check Quota Function (Memoized)
  const checkQuota = useCallback((token) => {
    fetchWithRetry('/api/checkQuota', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-App-Token': token,
        'Content-Type': 'application/json'
      }
    }).then(async res => {
      const data = await res.json();
      if (res.ok && data.status === 'Allowed') {
        setQuotaStatus("allowed");
        setChatCount(data.chatCount || 0);
        setMonthlyChatLimit(data.monthlyChatLimit || 300);
        initializeChatSession(token, data.preferredLanguage || 'en');
      } else {
        console.warn("Quota Blocked:", data);
        setQuotaStatus("blocked");
        if (data.error || data.message) setQuotaErrorMsg(data.error || data.message);
      }
    }).catch(e => {
      console.error("Quota check failed", e);
      setQuotaStatus("error");
      setQuotaErrorMsg(e.message);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Web Chat Store to intercept messages and charge 1 credit per message
  const chatStore = useMemo(() => {
    return createStore({}, ({ dispatch }) => next => async action => {
      // Intercept only when the user explicitly sends a message
      if (action.type === 'DIRECT_LINE/POST_ACTIVITY' && action.payload?.activity?.type === 'message') {
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            const token = await currentUser.getIdToken();
            const res = await fetchWithRetry('/api/trackMessage', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'X-App-Token': token,
                'Content-Type': 'application/json'
              }
            }, 1, 0); // Important: only try once so UI doesn't lag

            if (res.status === 429) {
              setQuotaExceeded(true);
              addLog("Message BLOCKED: Monthly quota exceeded.");
              return; // Halt message! It will not go to Microsoft.
            }
            if (!res.ok) {
              console.error("Failed to track message, blocking just in case.");
              return;
            }
            
            // Successfully tracked, increment frontend counter
            setChatCount(prev => prev + 1);

          } catch (e) {
            console.error("Tracker caught exception:", e);
            return;
          }
        }
      }

      // Proactive Welcome Message Trigger
      // When the Direct Line connection is fully established, send an invisible event 
      // to Copilot Studio to trigger the "Greeting" topic.
      if (action.type === 'DIRECT_LINE/CONNECT_FULFILLED') {
        dispatch({
          type: 'WEB_CHAT/SEND_EVENT',
          payload: {
            name: 'startConversation',
            value: { language: language } // Optional: Pass language in the event if needed
          }
        });
      }

      // Intercept incoming activities from the bot to strip markdown and trigger speech output (Option B: Voice Always On)
      if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY') {
        const { activity } = action.payload;
        if (activity && activity.type === 'message' && activity.from?.role === 'bot') {
          const cleanText = stripMarkdown(activity.text || activity.speak);
          activity.speak = cleanText;

          // If speech is enabled (Voice ON), directly synthesize audio with Azure Neural Voice
          if (speechEnabledRef.current && cleanText) {
            const ponyfill = activePonyfillRef.current;
            if (ponyfill && ponyfill.speechSynthesis && ponyfill.SpeechSynthesisUtterance) {
              try {
                unlockAudio();
                const utterance = new ponyfill.SpeechSynthesisUtterance(cleanText);
                const voices = ponyfill.speechSynthesis.getVoices ? ponyfill.speechSynthesis.getVoices() : [];
                const matchedVoice = selectVoice(voices);
                if (matchedVoice) {
                  utterance.voice = matchedVoice;
                }
                ponyfill.speechSynthesis.speak(utterance);
              } catch (err) {
                console.error("Error synthesizing speech response:", err);
              }
            }
          }
        }
      }

      return next(action); // Release message to Copilot Studio
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, unlockAudio]);

  const initializeChatSession = async (token, lang) => {
    try {
      // Hit our new Azure Proxy Endpoint to provision a token
      const res = await fetch('/api/chatProxy', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-App-Token': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language: lang })
      });

      if (res.status === 429) {
        setQuotaExceeded(true);
        addLog("Monthly message quota exceeded.");
        return;
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Proxy returned ${res.status}`);
      }

      const { token: directLineToken } = await res.json();

      setDirectLine(createDirectLine({ token: directLineToken }));
      setQuotaExceeded(false);
    } catch (err) {
      console.error("Chat initialization failed:", err);
      setQuotaStatus("error");
      setQuotaErrorMsg(`Chat Proxy Error: ${err.message}`);
    }
  };

  const handleStartNewConversation = useCallback(() => {
    unlockAudio();
    setDirectLine(null);
    setPonyfillFactory(null);
    setIsSessionActive(true);
    resetInactivityTimers();
    if (user) {
      user.getIdToken(true).then(token => {
        checkQuota(token);
      }).catch(e => console.error("Error starting new conversation token refresh:", e));
    }
  }, [user, checkQuota, resetInactivityTimers, unlockAudio]);

  const handleRetryLaunch = useCallback(() => {
    setQuotaStatus("loading");
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(token => {
        checkQuota(token);
      }).catch(err => {
        setQuotaStatus("error");
        setQuotaErrorMsg(`Retry failed: ${err.message}`);
      });
    } else {
      setQuotaStatus("loading");
    }
  }, [checkQuota]);


  const updateLanguagePreference = async (lang) => {
    unlockAudio();
    setLanguage(lang);
    setShowLanguageModal(false);

    if (!user) return;

    try {
      const token = await user.getIdToken();

      // Hit backend to save preference permanently
      await fetchWithRetry('/api/update-language', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-App-Token': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language: lang })
      });
      console.log("Language updated:", lang);

      // Critcal Fix: We MUST generate a new Direct Line session for the new bot!
      // This wipes the previous directLine connection and creates a fresh one for the other language bot.
      setDirectLine(null);
      initializeChatSession(token, lang);

    } catch (error) {
      console.error("Failed to save language:", error);
    }
  };

  // Handle Auth State Changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        console.log("User Authenticated:", currentUser.email);
        addLog(`Auth Success: ${currentUser.email}`);

        try {
          const token = await currentUser.getIdToken();

          // 1. Sync User to Backend & Get Language
          const syncRes = await fetchWithRetry('/api/login', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-App-Token': token,
              'Content-Type': 'application/json'
            }
          });

          if (syncRes.ok) {
            const data = await syncRes.json();
            console.log("Backend sync successful. Language:", data.preferredLanguage);

            // Set Language logic
            if (data.preferredLanguage) {
              setLanguage(data.preferredLanguage);
            } else {
              setShowLanguageModal(true); // First time user
            }

            // 2. Check Quota
            checkQuota(token);
          } else {
            const errJson = await syncRes.json();
            console.error("Backend sync failed:", syncRes.status, errJson);
            setQuotaStatus("error");
            const debugStr = errJson.debugInfo ? JSON.stringify(errJson.debugInfo) : "No Debug Info";
            addLog(`Backend Sync Failed: ${errJson.error} - Debug: ${debugStr}`);
          }
        } catch (error) {
          console.error("Token/Sync Error:", error);
          setQuotaStatus("error");
          addLog(`Sync Error: ${error.message}`);
        }
      } else {
        console.log("User Signed Out");
        addLog("User Signed Out");
        setQuotaStatus("loading");
        setLanguage(null);
        setDirectLine(null);
      }
    });

    return () => unsubscribe();
  }, [checkQuota]);

  // Handle Google OAuth Redirect Result (for Mobile / iOS Standalone PWA mode)
  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result && result.user) {
          console.log("Redirect login successful:", result.user.email);
          addLog(`Redirect Login Success: ${result.user.email}`);
        }
      })
      .catch((error) => {
        if (
          error.code === 'auth/popup-closed-by-user' || 
          error.code === 'auth/cancelled-popup-request'
        ) {
          return; // User intentionally cancelled or closed window
        }
        console.error("Redirect Login Error:", error);
        addLog(`Redirect Auth Error: ${error.message}`);
      });
  }, []);

  const handleGoogleLogin = async () => {
    try { unlockAudio(); } catch (e) {}
    try {
      console.log("Initiating Google Sign-In with popup...");
      addLog("Starting Google Popup Sign-In");
      await signInWithPopup(auth, googleProvider);
      console.log("Google popup login successful!");
      addLog("Google Popup Sign-In Successful");
    } catch (error) {
      console.warn("Google popup login error:", error.code, error.message);
      
      if (
        error.code === 'auth/popup-closed-by-user' || 
        error.code === 'auth/cancelled-popup-request'
      ) {
        console.log("User closed Google login window.");
        return; // User intentionally cancelled or closed window
      }

      // If popup was blocked by browser or restricted environment, try fallback to redirect
      if (
        error.code === 'auth/popup-blocked' || 
        error.code === 'auth/operation-not-allowed'
      ) {
        console.log("Popup blocked or not allowed, falling back to signInWithRedirect...");
        addLog("Popup blocked, trying Redirect fallback");
        try {
          await signInWithRedirect(auth, googleProvider);
        } catch (redirectErr) {
          console.error("Google Redirect Login Failed:", redirectErr);
          addLog(`Google Redirect Error: ${redirectErr.message}`);
          alert(`Google Sign-In Failed (${redirectErr.code || 'error'}): ${redirectErr.message}`);
        }
      } else {
        console.error("Google Login Failed:", error);
        addLog(`Google Login Error (${error.code}): ${error.message}`);
        alert(`Google Sign-In Error [${error.code || 'Error'}]: ${error.message}`);
      }
    }
  };


  const handleEmailAuth = async (e) => {
    e.preventDefault();
    unlockAudio();
    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
        addLog(`Sign Up Success: ${email}`);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        addLog(`Login Success: ${email}`);
      }
    } catch (error) {
      console.error("Email Auth Failed:", error);
      addLog(`Email Auth Error: ${error.message}`);
      alert(`Authentication Failed: ${error.message}`);
    }
  };

  const handleLogout = async () => {
    handleStopSpeaking();
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout Failed:", error);
    }
  };

  const handleUpgrade = async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      // Wait for Stripe Checkout Session to be created
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email
        })
      });

      if (!response.ok) throw new Error(await response.text());
      const { url } = await response.json();

      if (url) {
        // Set a flag in session storage before redirecting
        sessionStorage.setItem('awaitingStripeReturn', 'true');
        window.top.location.href = url; // BREAKOUT OF WIX IFRAME
      } else {
        alert("No checkout URL returned");
      }
    } catch (err) {
      console.error("Upgrade failed:", err);
      alert("Could not start checkout. Check console.");
    }
  };

  const handleBuyAddon = async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email,
          isAddon: true
        })
      });

      if (!response.ok) throw new Error(await response.text());
      const { url } = await response.json();

      if (url) {
        sessionStorage.setItem('awaitingStripeReturn', 'true');
        window.top.location.href = url;
      } else {
        alert("No checkout URL returned");
      }
    } catch (err) {
      console.error("Addon purchase failed:", err);
      alert("Could not start checkout for add-on. Check console.");
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const response = await fetchWithRetry('/api/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-App-Token': token
        },
        body: JSON.stringify({
          userId: user.uid
        })
      });

      if (!response.ok) throw new Error(await response.text());
      const { url } = await response.json();

      if (url) {
        window.top.location.href = url; // BREAKOUT OF WIX IFRAME
      } else {
        alert("Failed to load generic portal URL.");
      }
    } catch (err) {
      console.error("Portal access failed:", err);
      let errorMsg = err.message;
      try {
        const parsed = JSON.parse(err.message);
        errorMsg = parsed.error || err.message;
      } catch (e) { }
      alert(`Could not load billing portal: ${errorMsg}`);
    }
  };

  const handleResetPassword = async () => {
    if (!user || !user.email) return;
    try {
      await sendPasswordResetEmail(auth, user.email);
      alert(`Password reset email sent to ${user.email}! Please check your inbox.`);
      setShowDropdown(false);
    } catch (error) {
      console.error("Password Reset Failed:", error);
      alert(`Failed to send reset email: ${error.message}`);
    }
  };

  // Force token refresh if returning from Stripe Checkout
  useEffect(() => {
    const handleReturn = async () => {
      if (user && sessionStorage.getItem('awaitingStripeReturn') === 'true') {
        sessionStorage.removeItem('awaitingStripeReturn');
        console.log("Returned from Stripe, forcing token refresh to get new claims");
        addLog("Forcing Token Refresh for Custom Claims");

        try {
          // Pass true to force refresh and ignore cache
          const token = await user.getIdToken(true);
          // Re-check quota with new token
          checkQuota(token);
        } catch (e) {
          console.error("Failed to refresh token after Stripe return", e);
        }
      }
    };
    handleReturn();
  }, [user, checkQuota]);

  return (
    <div className="app-container">
      {/* LANGUAGE MODAL */}
      {showLanguageModal && (
        <div className="modal-overlay">
          <div className="card modal-content">
            <h2 className="card-title">Select Language / Seleccionar idioma</h2>
            <div className="modal-actions">
              <div className="modal-buttons">
                <button
                  className="btn btn-primary"
                  onClick={() => updateLanguagePreference('en')}
                >
                  English
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => updateLanguagePreference('es')}
                  style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                >
                  Español
                </button>
              </div>

              {language && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowLanguageModal(false)}
                >
                  Cancel / Cancelar
                </button>
              )}
            </div>
          </div>
        </div>
      )}



      {showInactivityWarning && (
        <div className="modal-overlay inactivity-warning-overlay" style={{ zIndex: 9999 }}>
          <div className="card modal-content inactivity-warning-modal" style={{ textAlign: 'center', maxWidth: '420px', padding: '2rem' }}>
            <h3 style={{ fontSize: '1.4rem', color: '#d95c5c', marginBottom: '1rem' }}>
              ⏰ {language === 'es' ? "¿Sigues ahí?" : "Are you still there?"}
            </h3>
            <p style={{ color: 'var(--text-primary)', marginBottom: '1.5rem', lineHeight: '1.5' }}>
              {language === 'es' 
                ? "Tu conversación activa de CareBridge se cerrará automáticamente en 5 minutos por inactividad."
                : "Your active CareBridge conversation will automatically close in 5 minutes due to inactivity."}
            </p>
            <button 
              className="btn btn-primary" 
              onClick={resetInactivityTimers}
              style={{ width: '100%', padding: '10px 15px' }}
            >
              {language === 'es' ? "Continuar conversación" : "Continue conversation"}
            </button>
          </div>
        </div>
      )}

      <header className="app-header">
        <h1>CareBridge Copilot</h1>
        {user && language && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div className="language-badge" style={{ marginTop: '0' }}>
              <span>Language / Idioma: <strong>{language === 'es' ? 'Español' : 'English'}</strong></span>
              <button
                onClick={() => setShowLanguageModal(true)}
                className="btn-change-lang"
              >
                Change
              </button>
            </div>

            <div className="user-menu-container">
              <button
                className="btn-user-menu"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                Profile Menu ▼
              </button>

              {showDropdown && (
                <div className="dropdown-menu">
                  {/* Let free users only reset password, active premium users can manage billing */}
                  {quotaStatus === 'allowed' && (
                    <button className="dropdown-item" onClick={handleManageSubscription}>💳 Manage Subscription</button>
                  )}
                  <button className="dropdown-item" onClick={handleResetPassword}>🔑 Reset Password</button>
                  <button className="dropdown-item danger" onClick={handleLogout}>🚪 Sign Out</button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {!user ? (
        <div className="card login-card">
          <h2 className="card-title">{isSignUp ? 'Create Account' : 'Welcome Back'}</h2>
          <form className="form-container" onSubmit={handleEmailAuth}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
              />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }}>
              {isSignUp ? 'Sign Up' : 'Login'}
            </button>
          </form>

          <div className="auth-divider">
            <span>OR</span>
          </div>

          <button onClick={handleGoogleLogin} className="btn btn-secondary" style={{ width: '100%', backgroundColor: '#fff', borderColor: '#ddd', color: '#444' }}>
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" style={{ width: '18px', marginRight: '10px' }} />
            Sign In with Google
          </button>

          <p className="text-center mt-md" style={{ fontSize: '0.9rem' }}>
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              style={{ background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', fontWeight: 'bold', marginLeft: '5px' }}
            >
              {isSignUp ? 'Login' : 'Sign Up'}
            </button>
          </p>
        </div>
      ) : (
        <div className="user-dashboard">
          <div className="welcome-section">
            <h3>Welcome back, {user.displayName || user.email}!</h3>
            <span className="status-badge active">Status: Active</span>
          </div>

          {!isSessionActive ? (
            <div className="card carebridge-start-card" style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
              <div className="carebridge-start-hero-wrapper" style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <DraggableAvatarPod 
                  visemeId={visemeId}
                  isSpeaking={isSpeaking}
                  isWaving={isAvatarWaving}
                  language={language}
                  onWaveTrigger={() => setIsAvatarWaving(true)}
                  onStopSpeaking={handleStopSpeaking}
                />
              </div>
              <h2 style={{ fontSize: '1.6rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>
                {language === 'es' ? "Conversación finalizada por inactividad" : "Conversation ended due to inactivity"}
              </h2>
              <p style={{ maxWidth: '550px', margin: '0 auto 1.5rem auto', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                {language === 'es' 
                  ? "Tu sesión anterior finalizó tras 10 minutos de inactividad para proteger tu privacidad. Permaneces conectado a CareBridge. Haz clic abajo para iniciar una nueva consulta."
                  : "Your previous session ended after 10 minutes of inactivity to protect your privacy. You remain logged in to CareBridge. Click below to start a new consultation."}
              </p>
              <button 
                className="btn btn-primary btn-start-session"
                onClick={handleStartNewConversation}
                style={{ padding: '12px 28px', fontSize: '1.1rem', background: 'var(--primary-color)', borderColor: 'var(--primary-color)' }}
              >
                {language === 'es' ? "💬 Iniciar Nueva Conversación" : "💬 Start New Conversation"}
              </button>
            </div>
          ) : (
            <div className="card">
              <h2 className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Assistant Chat ({language === 'es' ? 'Español' : 'English'})</span>
                {quotaStatus === 'allowed' && (
                  <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-color)', backgroundColor: 'var(--bg-color)', padding: '5px 15px', borderRadius: '20px', border: '1px solid #ddd' }}>
                    Usage: <strong style={{ color: chatCount >= monthlyChatLimit ? '#dc3545' : 'inherit' }}>{chatCount} / {monthlyChatLimit}</strong>
                  </span>
                )}
              </h2>
              <div className="chat-section">
                <div className="chat-container">
                  {/* --- Copilot Studio Chatbot Embed (Gated by Quota) --- */}

                  {quotaStatus === 'loading' && (
                    <div className="spinner-container">
                      <div className="spinner"></div>
                      <p>{language === 'es' ? "Verificando suscripción y conectando..." : "Verifying subscription and connecting..."}</p>
                      <button 
                        className="btn btn-secondary" 
                        onClick={handleRetryLaunch}
                        style={{ marginTop: '15px', fontSize: '0.85rem' }}
                      >
                        {language === 'es' ? "Reintentar Conexión" : "Retry Connection"}
                      </button>
                    </div>
                  )}

                  {quotaStatus === 'blocked' && (
                    <div className="quota-blocked">
                      <h3>Subscription Required</h3>
                      <p>Only subscribed users can access the CareBridge Copilot.</p>
                      <button className="btn btn-primary" onClick={handleUpgrade}>
                        Subscribe to Premium
                      </button>
                    </div>
                  )}

                  {quotaStatus === 'error' && (
                    <div className="quota-error">
                      <p>Could not verify subscription. Please refresh.</p>
                      <p style={{ color: 'red', marginTop: '10px', fontSize: '0.9rem' }}>DEBUG: {quotaErrorMsg}</p>
                      <button 
                        className="btn btn-secondary" 
                        onClick={handleRetryLaunch}
                        style={{ marginTop: '15px', fontSize: '0.85rem' }}
                      >
                        {language === 'es' ? "Reintentar Conexión" : "Retry Connection"}
                      </button>
                    </div>
                  )}

                  {quotaStatus === 'allowed' && quotaExceeded && (
                    <div className="quota-blocked" style={{ textAlign: 'center', padding: '2rem' }}>
                      <h3 style={{ color: '#dc3545', marginBottom: '1rem' }}>Monthly Limit Reached</h3>
                      <p style={{ marginBottom: '1.5rem', color: '#555' }}>You have used all {monthlyChatLimit} of your available chats for this cycle. Need more?</p>
                      <button 
                        className="btn btn-primary" 
                        onClick={handleBuyAddon}
                        style={{ padding: '10px 20px', fontSize: '1.1rem', backgroundColor: '#d97757', borderColor: '#d97757' }}
                      >
                        Purchase 100 Additional Chats
                      </button>
                      <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#888' }}>
                        Add-on chats will remain available until the end of your current billing cycle.
                      </p>
                    </div>
                  )}

                  {quotaStatus === 'allowed' && !quotaExceeded && directLine && (
                    <>
                      {isVoiceMode && (
                        <>
                          {/* 180px Floating Draggable 3D Avatar Pod */}
                          <DraggableAvatarPod 
                            visemeId={visemeId}
                            isSpeaking={isSpeaking}
                            isWaving={isAvatarWaving}
                            language={language}
                            onWaveTrigger={() => setIsAvatarWaving(true)}
                            onStopSpeaking={handleStopSpeaking}
                          />

                          <div className="voice-assistant-panel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="voice-panel-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <label htmlFor="voice-select" style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                                {language === 'es' ? "Voz:" : "Voice:"}
                              </label>
                              <select 
                                id="voice-select" 
                                className="voice-select-dropdown"
                                value={selectedVoice}
                                onChange={(e) => {
                                  handleStopSpeaking();
                                  const newVoice = e.target.value;
                                  setSelectedVoice(newVoice);
                                  selectedVoiceRef.current = newVoice;
                                }}
                              >
                                {language === 'es' ? (
                                  <>
                                    <option value="es-ES-ElviraNeural">Elvira (Español Femenino)</option>
                                    <option value="es-ES-AlvaroNeural">Alvaro (Español Masculino)</option>
                                  </>
                                ) : (
                                  <>
                                    <option value="en-US-JennyNeural">Jenny (US Female)</option>
                                    <option value="en-US-GuyNeural">Guy (US Male)</option>
                                  </>
                                )}
                              </select>
                            </div>

                            <div className="voice-panel-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {isSpeaking && (
                                <button
                                  className="voice-btn stop-speaking-panel-btn"
                                  onClick={handleStopSpeaking}
                                  title={language === 'es' ? "Detener voz" : "Stop speaking"}
                                >
                                  🛑 {language === 'es' ? "Detener" : "Stop"}
                                </button>
                              )}
                              <button 
                                className={`voice-btn speaker-btn ${!speechEnabled ? 'muted' : ''}`}
                                onClick={() => setSpeechEnabled(prev => !prev)}
                                title={speechEnabled ? "Mute bot voice" : "Unmute bot voice"}
                              >
                                <span className="speaker-icon">{speechEnabled ? "🔊" : "🔇"}</span>
                              </button>
                              <span className="speech-status">
                                {speechEnabled ? "Voice ON" : "Voice MUTED"}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                      <div className="webchat-container">
                        {!speechLoading ? (
                          <ReactWebChat
                            key={language} // IMPORTANT: Forces React to completely destroy the old chat window and erase history when language changes
                            directLine={directLine}
                            store={chatStore}
                            locale={language === 'es' ? 'es-ES' : 'en-US'}
                            webSpeechPonyfillFactory={ponyfillFactory}
                            selectVoice={selectVoice}
                            styleOptions={{
                              hideUploadButton: true,
                              bubbleBackground: '#f0ede5', // Fall theme beige
                              bubbleFromUserBackground: '#d97757', // Fall theme terracotta
                              bubbleFromUserTextColor: 'White',
                              sendBoxHeight: 50,
                              // Ensure Suggested Prompts / Actions render visibly
                              suggestedActionLayout: 'carousel',
                              suggestedActionBackground: '#ffffff',
                              suggestedActionBorderColor: '#a4ad92',
                              suggestedActionBorderRadius: 4,
                              suggestedActionTextColor: '#4a4238',
                            }}
                          />
                        ) : (
                          <div className="spinner-container">
                            <div className="spinner"></div>
                            <p>Initializing Azure Speech Services...</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
